/**
 * Command words, explained (bug fix 1, spec 9 §3): what each asks the student to do, in our
 * own short generic words — no board's definitions are copied — and how often it starts a
 * question in the student's past papers. Pure.
 */
import type { Exemplar } from "../api/api";

export const DEFINITIONS: Record<string, string> = {
  state: "give a short answer, with no explanation",
  give: "give a short answer, with no explanation",
  name: "give the name of something",
  identify: "pick out or name something from what you're given",
  describe: "say what something is like or what happens",
  explain: "give reasons, or say how or why something happens",
  suggest: "put forward a sensible idea, using what you know",
  calculate: "work out a number, showing your working",
  determine: "work out an answer from the information given",
  estimate: "give a sensible approximate value",
  compare: "say what is the same and what is different",
  evaluate: "weigh up the evidence and come to a judgement",
  discuss: "set out the points on different sides",
  justify: "give reasons that support an answer",
  sketch: "draw roughly, showing the main features",
  plot: "mark points on a graph from data",
  draw: "draw accurately, labelled where needed",
  complete: "fill in what is missing",
  "show that": "prove the given result, step by step",
  predict: "say what will happen, from what you know",
  deduce: "reach a conclusion from the information given",
};

export function definition(word: string): string | null {
  return DEFINITIONS[word.trim().toLowerCase()] ?? null;
}

/** The pipeline's `_command_of`: the first word that is a command word, else the first
 * alphabetic word (a stem may start with its "0 1 . 1" numbering, not the verb). */
export function commandOf(text: string, words: string[]): string {
  const known = new Map(words.map((w) => [w.toLowerCase(), w]));
  const tokens = text.split(/\s+/).map((t) => t.replace(/^[.,:;()[\]]+|[.,:;()[\]]+$/g, ""));
  for (const t of tokens) {
    const hit = known.get(t.toLowerCase());
    if (hit) return hit;
  }
  return tokens.find((t) => /^[A-Za-z]+$/.test(t)) ?? "";
}

/** How many questions each command word starts, patterns only, most used first. */
export function commandCounts(
  exemplars: Exemplar[],
  words: string[],
): { word: string; count: number }[] {
  const counts = new Map(words.map((w) => [w, 0]));
  for (const e of exemplars) {
    if (e.pattern === false) continue;
    const c = commandOf(e.text, words);
    if (counts.has(c)) counts.set(c, (counts.get(c) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([word, count]) => ({ word, count }))
    .sort((a, b) => b.count - a.count || a.word.localeCompare(b.word));
}
